import { ShieldCheck, User, Building2, Info } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { BusinessProfileForm } from "@/components/profile/BusinessProfileForm";
import { NotificationPreferences } from "@/components/notifications/NotificationPreferences";
import { BillingSettings } from "@/components/billing/BillingSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between border-b border-border py-3 last:border-0">
    <span className="flex items-center gap-2.5 text-sm text-muted-foreground"><Icon className="h-4 w-4 text-accent" /> {label}</span>
    <span className="text-sm font-medium text-foreground">{value}</span>
  </div>
);

export default function Settings() {
  const { user, workspace, workspaceRole, platformRole } = useAuth();
  const isStaff = Boolean(platformRole);
  const [params] = useSearchParams();
  const allowed = isStaff ? ["account"] : ["business", "notifications", "billing", "account"];
  const requested = params.get("tab");
  const defaultTab = allowed.includes(requested) ? requested : (isStaff ? "account" : "business");

  return (
    <AppShell>
      <div className="space-y-6" data-testid="settings-page">
        <PageHeader eyebrow="AgentHQ · Account" title="Settings" description="Manage your account and professional business profile." />

        <Tabs defaultValue={defaultTab}>
          <TabsList>
            {!isStaff && <TabsTrigger value="business" data-testid="settings-tab-business">Business Profile</TabsTrigger>}
            {!isStaff && <TabsTrigger value="notifications" data-testid="settings-tab-notifications">Notifications</TabsTrigger>}
            {!isStaff && <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger>}
            <TabsTrigger value="account" data-testid="settings-tab-account">Account</TabsTrigger>
          </TabsList>

          {!isStaff && (
            <TabsContent value="business" className="mt-6">
              <BusinessProfileForm />
            </TabsContent>
          )}

          {!isStaff && (
            <TabsContent value="notifications" className="mt-6">
              <NotificationPreferences />
            </TabsContent>
          )}

          {!isStaff && (
            <TabsContent value="billing" className="mt-6">
              <BillingSettings />
            </TabsContent>
          )}

          <TabsContent value="account" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
              <CardContent className="pt-0">
                <Row icon={User} label="Name" value={`${user?.first_name || ""} ${user?.last_name || ""}`} />
                <Row icon={User} label="Email" value={user?.email} />
                {isStaff ? (
                  <Row icon={ShieldCheck} label="Platform role" value={platformRole === "super_admin" ? "Super Admin" : "Support Admin"} />
                ) : (
                  <>
                    <Row icon={ShieldCheck} label="Role" value={workspaceRole || "agent"} />
                    {workspace && <Row icon={Building2} label="Workspace" value={workspace.name} />}
                  </>
                )}
              </CardContent>
            </Card>
            {isStaff && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
                <Info className="h-4 w-4" /> Platform staff accounts are workspace-less and do not hold an agent business profile.
              </div>
            )}
            <p className="mt-4 text-xs text-muted-foreground">Full account &amp; preference settings arrive in a later phase.</p>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
